/**
 * Entry Patterns Mock
 *
 * Four common systematic entry patterns shown as cards with
 * character traits: win rate tendency, trade frequency, and
 * best market condition.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faRotate,
  faUpRightFromSquare,
  faCodeBranch,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Pattern {
  name: string;
  icon: IconDefinition;
  color: string;
  example: string;
  winRate: string;
  frequency: string;
  bestIn: string;
}

const patterns: Pattern[] = [
  {
    name: 'Trend Following',
    icon: faArrowTrendUp,
    color: '#3b82f6',
    example: 'EMA(9) crosses above EMA(21)',
    winRate: 'Low-moderate (35-50%)',
    frequency: 'Moderate',
    bestIn: 'Trending markets',
  },
  {
    name: 'Mean Reversion',
    icon: faRotate,
    color: '#8b5cf6',
    example: 'RSI(14) crosses above 30',
    winRate: 'High (55-70%)',
    frequency: 'Moderate-high',
    bestIn: 'Ranging markets',
  },
  {
    name: 'Breakout',
    icon: faUpRightFromSquare,
    color: '#f59e0b',
    example: 'Close > Donchian(50) upper',
    winRate: 'Low (25-40%)',
    frequency: 'Low',
    bestIn: 'Post-consolidation',
  },
  {
    name: 'Divergence',
    icon: faCodeBranch,
    color: '#22c55e',
    example: 'Price new high + RSI lower high',
    winRate: 'Moderate (45-55%)',
    frequency: 'Low',
    bestIn: 'Trend exhaustion',
  },
];

function Trait({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.25rem' }}>
      <span style={{
        fontSize: '0.5625rem', color: '#6b7280',
        textTransform: 'uppercase', letterSpacing: '0.03em',
      }}>{label}</span>
      <span style={{
        fontSize: '0.5625rem', color: '#d1d5db', fontWeight: 500,
        textAlign: 'right',
      }}>{value}</span>
    </div>
  );
}

export default function EntryPatternsMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Common Entry Patterns
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
        }}>
          {patterns.map((p) => (
            <div key={p.name} style={{
              padding: '0.625rem', background: '#181825',
              border: `1px solid ${p.color}20`, borderRadius: 8,
            }}>
              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                marginBottom: '0.4rem',
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: `${p.color}15`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <FontAwesomeIcon icon={p.icon} style={{ fontSize: '0.55rem', color: p.color }} />
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: p.color }}>
                  {p.name}
                </div>
              </div>

              {/* Example rule */}
              <div style={{
                fontSize: '0.5625rem', color: '#9ca3af', marginBottom: '0.4rem',
                fontFamily: 'ui-monospace, monospace',
                padding: '0.25rem 0.4rem', background: 'rgba(255,255,255,0.03)',
                borderRadius: 3,
              }}>
                {p.example}
              </div>

              {/* Traits */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                <Trait label="Win rate" value={p.winRate} />
                <Trait label="Frequency" value={p.frequency} />
                <Trait label="Best in" value={p.bestIn} />
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#3b82f6', fontWeight: 600 }}>Note: </span>
          Win rates and frequencies are typical ranges, not guarantees.
          Each pattern's character should match your exit strategy.
        </div>
      </div>
      <div className="platform-mock-caption">
        Different entry patterns produce different trade distributions. Choose the pattern that matches your thesis.
      </div>
    </div>
  );
}
